import React, { type FC, useCallback, useMemo } from "react"
import { useRouter } from "next/router"
import { FiltersPage as FiltersPageBase } from "@/components/Filter/Filters"
import { StyledFilterGroup } from "@/components/Filter/StyledFilter"
import { Select } from "@/components/ui/Select/Select"
import { MANUAL_FILTER_KEYS } from "@/hooks/filterCatalog/constants"
import { useAppDispatch, useAppSelector } from "@/hooks/redux"
import { SortType } from "@/types"
import type { SelectItemsType } from "@/ui/Select/types"
import { getUnique } from "@/utils/common/helpers"
import { type OnChangeFilterParamPropsType } from "../Filter/types"

export const FiltersPage: FC = () => {
  const router = useRouter()
  const dispatch = useAppDispatch()
  const { filters, filtersChecked, priceRange, sortBySelected, sortBy } =
    useAppSelector((state) => state.favorites.filter)

  const checkedParams = useMemo(
    () =>
      getUnique(
        Object.entries(filtersChecked).reduce(
          (arr: string[], [uuidFilter, params]) =>
            Object.hasOwn(MANUAL_FILTER_KEYS, uuidFilter)
              ? arr
              : [...arr, ...params],
          [],
        ),
      ),
    [filtersChecked],
  )

  // отделено от checkedParams, хотя это тоже фильтр
  // тк в url заносим значения в поле store вместо params
  const checkedStores = useMemo(
    () => filtersChecked[MANUAL_FILTER_KEYS.store] || [],
    [filtersChecked],
  )
  const checkedCount =
    checkedParams.length +
    checkedStores.length +
    (priceRange.isSelected ? 1 : 0)

  const isExist = checkedCount > 0

  const sortedItems: SelectItemsType[] = useMemo(() => {
    let c: SelectItemsType[] = []
    if (!!sortBy) {
      c = Object.entries(sortBy).map(([, sort]) => {
        const item: SortType = { ...sort }
        return {
          value: item.value,
          name: item.name,
          icon: item.icon,
        }
      })
    }
    return c
  }, [sortBy])

  const clearCheckedFilters = useCallback(() => {
    const url = new URL(window.location.href)
    url.searchParams.forEach((value, key) => {
      if (["group"].includes(key)) {
        return
      }
      url.searchParams.delete(key)
    })

    void router.replace(url, undefined, { shallow: true, scroll: true })
  }, [])

  const onChangeParamsHandler = useCallback(
    ({
      uuidParam,
      uuidFilter,
      state: checked,
    }: OnChangeFilterParamPropsType) => {
      const currentFilter = filtersChecked[uuidFilter] || []

      const newValues = checked
        ? getUnique([...currentFilter, uuidParam])
        : currentFilter.filter((f) => f !== uuidParam)

      const url = new URL(window.location.href)
      url.searchParams.delete("page")
      url.searchParams.set("params", String(newValues))

      void router.push(url.toString(), undefined, {
        shallow: true,
        scroll: true,
      })
    },
    [filtersChecked],
  )

  const onClearFilterHandler = useCallback(
    ({ uuidFilter }: { uuidFilter: string }) => {
      const url = new URL(window.location.href)
      url.searchParams.delete("page")
      url.searchParams.delete(uuidFilter)

      void router.push(url.toString(), undefined, {
        shallow: true,
        scroll: true,
      })
    },
    [],
  )

  const onClearPriceHandle = useCallback(() => {
    const url = new URL(window.location.href)
    url.searchParams.delete("page")
    url.searchParams.delete("price")

    void router.push(url.toString(), undefined, {
      shallow: true,
      scroll: true,
    })
  }, [dispatch])

  const onAfterChangePriceHandle = useCallback((value?: number[]) => {
    if (!value) {
      return
    }

    const url = new URL(window.location.href)
    url.searchParams.delete("page")
    url.searchParams.set("price", `${[value[0], value[1]].join("-")}`)

    void router.push(url.toString(), undefined, {
      shallow: true,
      scroll: true,
    })
  }, [])

  const onSelectSortByHandler = (value: string): void => {
    const url = new URL(window.location.href)
    url.searchParams.delete("page")
    url.searchParams.set("sortby", String(value))

    void router.push(url.toString(), undefined, {
      shallow: true,
      scroll: true,
    })
  }

  const sortElement = (
    <StyledFilterGroup>
      <Select
        variant={"small"}
        items={sortedItems}
        isIcon={true}
        ariaLabel={"sorted"}
        initialValue={sortBySelected || undefined}
        onSelectValue={onSelectSortByHandler}
      />
    </StyledFilterGroup>
  )

  return (
    <FiltersPageBase
      withViewControl={false}
      filters={filters}
      filtersChecked={filtersChecked}
      priceRange={priceRange}
      isExist={isExist}
      clearCheckedFilters={clearCheckedFilters}
      checkedCount={checkedCount}
      onChangeParams={onChangeParamsHandler}
      onClearFilter={onClearFilterHandler}
      sortElement={sortElement}
      radioElement
      onClearPrice={onClearPriceHandle}
      onAfterChangePrice={onAfterChangePriceHandle}
      visibleLimit={0}
    />
  )
}
